import { ClientLoggingService } from '../../../../shared/client-logging.service';
import {
  Component,
  Input,
  OnInit,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  inject,
  ViewChild,
  ElementRef,
} from '@angular/core';

import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { GoogleMapsService } from '../../../../shared/google-maps.service';

/**
 * Component for displaying project location on an interactive Google Map.
 *
 * Issue #236: Google Maps Integration for Project Address
 *
 * Features:
 * - Displays interactive Google Map
 * - Shows project location marker
 * - Handles loading, error, and "no address" states
 * - Responsive Material Design card
 *
 * See: docs/05-tdd/google-maps-integration.md
 */
@Component({
  selector: 'app-project-location-map',
  imports: [MatCardModule, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './project-location-map.component.html',
  styleUrls: ['./project-location-map.component.scss'],
})
export class ProjectLocationMapComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  private googleMapsService = inject(GoogleMapsService);

  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef<HTMLDivElement>;

  /**
   * Map center coordinates.
   * If null, map shows "No address" state.
   */
  @Input() center: { lat: number; lng: number } | null = null;

  /**
   * Map zoom level (default: 16 for street-level view).
   */
  @Input() zoom: number = 16;

  /**
   * Formatted address to display in marker tooltip.
   */
  @Input() address: string = '';

  // Component state
  map: google.maps.Map | null = null;
  marker: google.maps.marker.AdvancedMarkerElement | null = null;
  loading = true;
  error = false;

  // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method
  ngOnInit(): void {
    // Map initialization moved to ngAfterViewInit to ensure ViewChild is available
  }

  async ngAfterViewInit(): Promise<void> {
    await this.initializeMap();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    // Update map when center changes
    if (changes['center'] && this.map) {
      if (this.center) {
        this.updateMapLocation();
      } else {
        this.clearMap();
      }
    }
  }

  /**
   * Initializes the Google Map.
   */
  private async initializeMap(): Promise<void> {
    try {
      ClientLoggingService.log('INFO', '[ProjectLocationMap] Initializing map...');

      // Load Google Maps API
      await this.googleMapsService.loadGoogleMaps();

      // Get map container element using ViewChild
      const mapElement = this.mapContainer?.nativeElement;
      if (!mapElement) {
        throw new Error('Map container element not found');
      }

      // Default center (San Francisco) if no address provided
      const defaultCenter = { lat: 37.7749, lng: -122.4194 };
      const initialCenter = this.center || defaultCenter;
      const initialZoom = this.center ? this.zoom : 12;

      // Create map (mapId required for AdvancedMarkerElement)
      this.map = new google.maps.Map(mapElement, {
        center: initialCenter,
        zoom: initialZoom,
        mapTypeControl: false, // Hide map type control
        streetViewControl: false, // Hide street view control
        fullscreenControl: true, // Keep fullscreen control
        zoomControl: true, // Keep zoom control
        gestureHandling: 'greedy', // Allow one-finger pan on mobile
        mapId: 'project-location-map', // Required for AdvancedMarkerElement
      });

      // Add marker if center is provided
      if (this.center) {
        this.addMarker();
      }

      this.loading = false;
      ClientLoggingService.log('INFO', '[ProjectLocationMap] Map initialized successfully');
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ProjectLocationMap] Failed to initialize map:', error);
      this.error = true;
      this.loading = false;
    }
  }

  /**
   * Updates the map center and marker when address changes.
   */
  private updateMapLocation(): void {
    if (!this.map || !this.center) {
      ClientLoggingService.log('WARN', '[ProjectLocationMap] Cannot update location - map or center missing');
      return;
    }

    ClientLoggingService.log('INFO', '[ProjectLocationMap] Updating map location:', this.center);

    // Animate to new center
    this.map.panTo(this.center);
    this.map.setZoom(this.zoom);

    // Update or add marker
    this.addMarker();
  }

  /**
   * Adds or updates the location marker using AdvancedMarkerElement.
   * Migration from deprecated google.maps.Marker (Issue #1178).
   */
  private addMarker(): void {
    if (!this.map || !this.center) return;

    // Remove existing marker
    if (this.marker) {
      this.marker.map = null;
    }

    // Create new AdvancedMarkerElement
    this.marker = new google.maps.marker.AdvancedMarkerElement({
      position: this.center,
      map: this.map,
      title: this.address || 'Project Location',
    });

    ClientLoggingService.log('INFO', '[ProjectLocationMap] Marker added at:', this.center);
  }

  /**
   * Clears the marker from the map.
   */
  private clearMap(): void {
    if (this.marker) {
      this.marker.map = null;
      this.marker = null;
    }
  }

  ngOnDestroy(): void {
    // Clean up marker
    this.clearMap();

    // Map will be garbage collected
    this.map = null;
  }
}
